import React from 'react'
import TrueChip from '../TrueChip'
import { FalseIcon } from './Styled'
import { int2HexWithPadAndPrefix, int2HexWithPrefix, round } from '../../Utils/Numbers'
import * as R from 'ramda'
import { datetime } from '../../../Common/Utils/Datetime'
import Config from '../../../Core/Config'
import getStorage from '../../../Common/Services/Storage'
import { toast } from 'react-toastify'
import i18next from 'i18next'
import ReactJson from 'react-json-view'
import { ContentCopy, Delete, Edit } from '@mui/icons-material'
import Download from '@mui/icons-material/Download'

const storageService = getStorage()

export const createColumnsProps = (columns, listDisplay) =>
  columns
    .map((column) => ({
      ...column,
      visible: listDisplay.indexOf(column.id) !== -1,
    }))
    .sort((a, b) => {
      if (
        listDisplay.indexOf(a.id) !== -1 &&
        listDisplay.indexOf(b.id) !== -1
      ) {
        return listDisplay.indexOf(a.id) - listDisplay.indexOf(b.id)
      } else if (listDisplay.indexOf(a.id) !== -1) {
        return -1
      } else {
        return 1
      }
    })

export const createColumnsPropsWithStorage = (
  columns,
  listDisplay,
  storedColumns,
) =>
  columns
    .map((column) => ({
      ...column,
      visible: storedColumns
        ? storedColumns?.find(R.propEq('id', column.id))?.visible
        : listDisplay.indexOf(column.id) !== -1,
    }))
    .sort((a, b) => {
      if (storedColumns) {
        return storedColumns.findIndex(R.propEq('id', a.id)) >
          storedColumns.findIndex(R.propEq('id', b.id))
          ? 1
          : -1
      } else {
        if (
          listDisplay.indexOf(a.id) !== -1 &&
          listDisplay.indexOf(b.id) !== -1
        ) {
          return listDisplay.indexOf(a.id) - listDisplay.indexOf(b.id)
        } else if (listDisplay.indexOf(a.id) !== -1) {
          return -1
        } else {
          return 1
        }
      }
    })

export const getDisplayValue = (fieldsMapping, record, field, column) => {
  return fieldsMapping[field]
    ? fieldsMapping[field](record, 'jsx')
    : getValue(record, field, column)
}

export const getCsvValue = (fieldsMapping, record, field, column) => {
  return fieldsMapping[field]
    ? fieldsMapping[field](record, 'csv')
    : getValue(record, field, column, 'csv')
}

export const getValue = (record, field, column = null, format = 'jsx') => {
  const raw = field
    .split('.')
    .reduce((acc, curr) => (acc ? acc[curr] : null), record)
  if (column && column.datetime) {
    return raw ? column.datetimeFormat ? datetime(raw).format(column.datetimeFormat) : datetime(raw).format('Y-MM-DD HH:mm:ss') : null
  } else if (column && column.date) {
    return raw ? datetime(raw).format('Y-MM-DD') : null
  } else if (column && column.hex) {
    if (format === 'jsx' || column.exportHex) return R.isNil(raw) ? null : (column.hexPad ? int2HexWithPadAndPrefix(column.hexPad)(raw) : int2HexWithPrefix(raw))
    else return raw
  } else if (column && column.boolean) {
    if (format === 'jsx') return raw ? <TrueChip /> : <FalseIcon />
    else return raw ? 'true' : 'false'
  } else if (column && column.decimal && column.round) {
    return round(raw, column.round)
  } else if (R.is(Object, raw)) {
    return (
      <ReactJson
        src={raw}
        collapsed={0}
        theme={'monokai'}
        name="ROOT"
        enableClipboard={(copy) => {
          navigator.clipboard.writeText(JSON.stringify(copy.src, null, '\t'))
        }}
      />
    )
  }
  return raw
}

export const checkStorageInvalidation = (storageKey, id) => {
  if (
    Config.localStorageInvalidation[storageKey] &&
    storageService.get(storageKey)
  ) {
    if (typeof Config.localStorageInvalidation[storageKey] === 'function') {
      const res = Config.localStorageInvalidation[storageKey](storageKey)
      if (res === 'REMOVE') {
        storageService.remove(`${id}_CHANGELIST_TABLE_SETTINGS`)
        toast.warn(
          i18next.t('common:ui.ForceResetChangelistTableSettingsWarning'),
        )
      }
    } else {
      const storageTimestamp = storageService.getNested(
        storageKey,
        'timestamp',
        0,
      )
      if (storageTimestamp < Config.localStorageInvalidation[storageKey]) {
        storageService.remove(`${id}_CHANGELIST_TABLE_SETTINGS`)
        toast.warn(
          i18next.t('common:ui.ForceResetChangelistTableSettingsWarning'),
        )
      }
    }
  }
}

export const editAction = (permFunc, condFunc) => {
  const obj = {
    id: 'EDIT',
    label: i18next.t('common:generic.Edit'),
    icon: <Edit />,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  if (condFunc) {
    obj.cond = condFunc
  }
  return obj
}

export const deleteAction = (permFunc, showIcon = true, id = 'DELETE', condFunc) => {
  const obj = {
    id,
    label: i18next.t('common:generic.Delete'),
    icon: showIcon ? <Delete /> : null,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  if (condFunc) {
    obj.cond = condFunc
  }
  return obj
}

export const cloneAction = (permFunc) => {
  const obj = {
    id: 'CLONE',
    label: i18next.t('common:generic.Clone'),
    icon: <ContentCopy />,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  return obj
}

export const downloadAction = (permFunc, showIcon = true) => {
  const obj = {
    id: 'DOWNLOAD',
    label: i18next.t('common:generic.Download'),
    icon: showIcon ? <Download /> : null,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  return obj
}
